"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "communityplan-right-sidebar-collapsed";

type RightSidebarContextValue = {
  collapsed: boolean;
  toggle: () => void;
};

const RightSidebarContext = createContext<RightSidebarContextValue | null>(null);

/**
 * Holds right sidebar open/closed state at app root so it persists across
 * route changes (dashboard, members, events, etc.). Persists to localStorage.
 *
 * Uses false as the initial SSR-safe default and restores the persisted value
 * after hydration to avoid hydration mismatches.
 */
export function RightSidebarProvider({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const hydrated = useRef(false);

  // Restore persisted state after hydration
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "true") {
      setCollapsed(true);
    }
    hydrated.current = true;
  }, []);

  // Persist changes to localStorage (skip the initial hydration restore)
  useEffect(() => {
    if (!hydrated.current) return;
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const toggle = useCallback(() => {
    setCollapsed((c) => !c);
  }, []);

  return (
    <RightSidebarContext.Provider value={{ collapsed, toggle }}>
      {children}
    </RightSidebarContext.Provider>
  );
}

export function useRightSidebar(): RightSidebarContextValue {
  const ctx = useContext(RightSidebarContext);
  if (!ctx) {
    throw new Error("useRightSidebar must be used within RightSidebarProvider");
  }
  return ctx;
}
